import { useEffect, useState } from "react";
import {
  Plus,
  Edit2,
  Trash2,
  Save,
  X,
  Search,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Building2,
} from "lucide-react";
import { organizacaoService } from "../../services/apiService";
import { useNotification } from "../../hooks/useNotification";
import { useConfirmDialog } from "../../hooks/useConfirmDialog";
import PageContainer from "../../components/PageContainer";
import { formatarCnpj } from "../../utils/FormatarDocumento";

const ESTADOS_BRASIL = [
  "AC",
  "AL",
  "AP",
  "AM",
  "BA",
  "CE",
  "DF",
  "ES",
  "GO",
  "MA",
  "MT",
  "MS",
  "MG",
  "PA",
  "PB",
  "PR",
  "PE",
  "PI",
  "RJ",
  "RN",
  "RS",
  "RO",
  "RR",
  "SC",
  "SP",
  "SE",
  "TO",
];

const Organizacao = () => {
  const [loading, setLoading] = useState(false);
  const [organizacoes, setOrganizacoes] = useState([]);
  const [showOrganizacaoForm, setShowOrganizacaoForm] = useState(false);
  const [editingOrganizacao, setEditingOrganizacao] = useState(null);
  const [organizacaoForm, setOrganizacaoForm] = useState({
    nome: "",
    nomeFantasia: "",
    cnpj: "",
    cidade: "",
    estado: "",
  });
  const [organizacaoSearchTerm, setOrganizacaoSearchTerm] = useState("");
  const [paginacao, setPaginacao] = useState({
    paginaAtual: 1,
    itensPorPagina: 6,
  });

  const { showError, showSuccess } = useNotification();
  const confirmDialog = useConfirmDialog();

  const loadOrganizacoes = async (nome = "") => {
    setLoading(true);
    try {
      const data = await organizacaoService.listarOrganizacoes(nome || undefined);
      setOrganizacoes(data || []);
      setPaginacao((prev) => ({ ...prev, paginaAtual: 1 }));
    } catch (err) {
      setOrganizacoes([]);
      showError(err.message || "Erro ao carregar organizações");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrganizacoes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = (e) => {
    const value = e.target.value;
    setOrganizacaoSearchTerm(value);

    const timeoutId = setTimeout(() => {
      loadOrganizacoes(value.trim());
    }, 300);

    return () => clearTimeout(timeoutId);
  };

  const totalOrganizacoes = organizacoes.length;
  const totalPaginas = Math.ceil(totalOrganizacoes / paginacao.itensPorPagina);
  const indiceInicial = (paginacao.paginaAtual - 1) * paginacao.itensPorPagina;
  const indiceFinal = indiceInicial + paginacao.itensPorPagina;
  const organizacoesPaginadas = organizacoes.slice(indiceInicial, indiceFinal);

  const irParaPrimeiraPagina = () => {
    setPaginacao((prev) => ({ ...prev, paginaAtual: 1 }));
  };

  const irParaUltimaPagina = () => {
    setPaginacao((prev) => ({ ...prev, paginaAtual: totalPaginas }));
  };

  const irParaPaginaAnterior = () => {
    setPaginacao((prev) => ({
      ...prev,
      paginaAtual: Math.max(1, prev.paginaAtual - 1),
    }));
  };

  const irParaProximaPagina = () => {
    setPaginacao((prev) => ({
      ...prev,
      paginaAtual: Math.min(totalPaginas, prev.paginaAtual + 1),
    }));
  };

  const irParaPagina = (numeroPagina) => {
    setPaginacao((prev) => ({ ...prev, paginaAtual: numeroPagina }));
  };

  const gerarNumerosPaginas = () => {
    const paginas = [];
    const maxPaginasVisiveis = 5;
    let inicio = Math.max(
      1,
      paginacao.paginaAtual - Math.floor(maxPaginasVisiveis / 2)
    );
    let fim = Math.min(totalPaginas, inicio + maxPaginasVisiveis - 1);

    if (fim - inicio < maxPaginasVisiveis - 1) {
      inicio = Math.max(1, fim - maxPaginasVisiveis + 1);
    }

    for (let i = inicio; i <= fim; i++) {
      paginas.push(i);
    }

    return paginas;
  };

  const handleOrganizacaoSubmit = async () => {
    if (!organizacaoForm.nome.trim()) {
      showError("Nome da organização é obrigatório");
      return;
    }
    if (!organizacaoForm.nomeFantasia.trim()) {
      showError("Nome fantasia é obrigatório");
      return;
    }
    if (!organizacaoForm.cnpj.trim()) {
      showError("CNPJ é obrigatório");
      return;
    }
    if (!organizacaoForm.cidade.trim()) {
      showError("Cidade é obrigatória");
      return;
    }
    if (!organizacaoForm.estado.trim()) {
      showError("Estado é obrigatório");
      return;
    }
    if (organizacaoForm.estado.trim().length !== 2) {
      showError("Estado deve ser a UF com 2 letras");
      return;
    }

    setLoading(true);

    try {
      const dadosOrganizacao = {
        ...organizacaoForm,
        cidade: organizacaoForm.cidade.trim(),
        estado: organizacaoForm.estado.trim().toUpperCase(),
        cnpj: organizacaoForm.cnpj.replace(/\D/g, ""),
      };

      if (editingOrganizacao) {
        await organizacaoService.atualizarOrganizacao(
          editingOrganizacao.id,
          dadosOrganizacao
        );
        showSuccess("Organização atualizada com sucesso!");
      } else {
        await organizacaoService.cadastrarOrganizacao(dadosOrganizacao);
        showSuccess("Organização criada com sucesso!");
      }

      await loadOrganizacoes(organizacaoSearchTerm);
      handleOrganizacaoCancel();
    } catch (err) {
      showError(err.message || "Erro ao salvar organização");
    } finally {
      setLoading(false);
    }
  };

  const handleNovaOrganizacao = () => {
    setEditingOrganizacao(null);
    setOrganizacaoForm({
      nome: "",
      nomeFantasia: "",
      cnpj: "",
      cidade: "",
      estado: "",
    });
    setShowOrganizacaoForm(true);
  };

  const handleEditarOrganizacao = (item) => {
    setEditingOrganizacao(item);
    setOrganizacaoForm({
      nome: item.nome || "",
      nomeFantasia: item.nomeFantasia || "",
      cnpj: formatarCnpj(item.cnpj || ""),
      cidade: item.cidade || "",
      estado: item.estado || "",
    });
    setShowOrganizacaoForm(true);
  };

  const handleExcluirOrganizacao = async (item) => {
    const confirmed = await confirmDialog({
      title: "Confirmar",
      description: `Tem certeza que deseja excluir a organização "${item.nome}"?`,
    });

    if (!confirmed) {
      return;
    }

    setLoading(true);

    try {
      await organizacaoService.deletarOrganizacao(item.id);
      showSuccess("Organização excluída com sucesso!");
      await loadOrganizacoes(organizacaoSearchTerm);
    } catch (err) {
      showError(err.message || "Erro ao excluir organização");
    } finally {
      setLoading(false);
    }
  };

  const handleOrganizacaoCancel = () => {
    setShowOrganizacaoForm(false);
    setEditingOrganizacao(null);
    setOrganizacaoForm({
      nome: "",
      nomeFantasia: "",
      cnpj: "",
      cidade: "",
      estado: "",
    });
  };

  const handleOrganizacaoKeyPress = (e) => {
    if (e.key === "Enter") {
      handleOrganizacaoSubmit();
    }
  };

  return (
    <PageContainer className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Organizações</h1>
        <p className="text-gray-600">
          Visualize as organizações cadastradas na plataforma.
        </p>
      </div>

      <div className="mb-6 flex flex-col sm:flex-row gap-4 justify-between">
        <div className="relative flex-1 max-w-md">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Pesquisar por nome..."
            value={organizacaoSearchTerm}
            onChange={handleSearch}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <button
          onClick={handleNovaOrganizacao}
          disabled={loading}
          className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
        >
          <Plus size={20} />
          Nova Organização
        </button>
      </div>

      {showOrganizacaoForm && (
        <div className="mb-6 p-6 bg-gray-50 rounded-lg border">
          <h3 className="text-lg font-semibold mb-4">
            {editingOrganizacao ? "Editar Organização" : "Nova Organização"}
          </h3>

          <div>
            <div className="mb-4">
              <label
                htmlFor="organizacao-nome"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Nome da organização *
              </label>
              <input
                type="text"
                id="organizacao-nome"
                value={organizacaoForm.nome}
                onChange={(e) =>
                  setOrganizacaoForm({ ...organizacaoForm, nome: e.target.value })
                }
                onKeyPress={handleOrganizacaoKeyPress}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Digite o nome da organização"
                maxLength={100}
              />
            </div>

            <div className="mb-4">
              <label
                htmlFor="organizacao-nome-fantasia"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Nome fantasia *
              </label>
              <input
                type="text"
                id="organizacao-nome-fantasia"
                value={organizacaoForm.nomeFantasia}
                onChange={(e) =>
                  setOrganizacaoForm({
                    ...organizacaoForm,
                    nomeFantasia: e.target.value,
                  })
                }
                onKeyPress={handleOrganizacaoKeyPress}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Digite o nome fantasia"
                maxLength={100}
              />
            </div>

            <div className="mb-4">
              <label
                htmlFor="organizacao-cnpj"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                CNPJ *
              </label>
              <input
                type="text"
                id="organizacao-cnpj"
                value={organizacaoForm.cnpj}
                onChange={(e) =>
                  setOrganizacaoForm({
                    ...organizacaoForm,
                    cnpj: formatarCnpj(e.target.value),
                  })
                }
                onKeyPress={handleOrganizacaoKeyPress}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="00.000.000/0000-00"
                maxLength={18}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label
                  htmlFor="organizacao-cidade"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Cidade *
                </label>
                <input
                  type="text"
                  id="organizacao-cidade"
                  value={organizacaoForm.cidade}
                  onChange={(e) =>
                    setOrganizacaoForm({
                      ...organizacaoForm,
                      cidade: e.target.value,
                    })
                  }
                  onKeyPress={handleOrganizacaoKeyPress}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Digite a cidade"
                  maxLength={100}
                />
              </div>

              <div>
                <label
                  htmlFor="organizacao-estado"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Estado *
                </label>
                <select
                  id="organizacao-estado"
                  value={organizacaoForm.estado}
                  onChange={(e) =>
                    setOrganizacaoForm({
                      ...organizacaoForm,
                      estado: e.target.value,
                    })
                  }
                  onKeyDown={handleOrganizacaoKeyPress}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Selecione...</option>
                  {ESTADOS_BRASIL.map((uf) => (
                    <option key={uf} value={uf}>
                      {uf}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                onClick={handleOrganizacaoSubmit}
                disabled={
                  loading ||
                  !organizacaoForm.nome.trim() ||
                  !organizacaoForm.nomeFantasia.trim() ||
                  !organizacaoForm.cnpj.trim() ||
                  !organizacaoForm.cidade.trim() ||
                  !organizacaoForm.estado.trim()
                }
                className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
              >
                <Save size={16} />
                {loading ? "Salvando..." : "Salvar"}
              </button>

              <button
                onClick={handleOrganizacaoCancel}
                disabled={loading}
                className="bg-gray-500 hover:bg-gray-600 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
              >
                <X size={16} />
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow border">
        <div className="p-4 border-b flex justify-between items-center">
          <h3 className="text-lg font-semibold">Lista de Organizações</h3>
          <span className="text-sm text-gray-500">
            {totalOrganizacoes} registro(s) encontrado(s)
          </span>
        </div>

        {loading && organizacoes.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            Carregando organizações...
          </div>
        ) : organizacoes.length === 0 ? (
          <div className="p-10 text-center text-gray-500">
            <Building2 size={28} className="mx-auto mb-3 text-gray-400" />
            {organizacaoSearchTerm
              ? "Nenhuma organização encontrada"
              : "Nenhuma organização cadastrada"}
          </div>
        ) : (
          <>
            <div>
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Organização
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      CNPJ
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Cidade / UF
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {organizacoesPaginadas.map((item) => (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {item.nomeFantasia || item.nome}
                        </div>
                        <div className="text-xs text-gray-500">
                          Nome jurídico: {item.nome || "-"}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {item.cnpj ? formatarCnpj(item.cnpj) : "-"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        <div className="font-medium text-gray-900">
                          {item.cidade || "-"}
                        </div>
                        <div className="text-xs text-gray-500">
                          {item.estado ? `UF: ${item.estado}` : "UF: -"}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700">
                          {item.ativo ? "Ativa" : "Inativa"}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => handleEditarOrganizacao(item)}
                            disabled={loading}
                            className="text-blue-600 hover:text-blue-900 disabled:text-gray-400 p-1 rounded transition-colors"
                            title="Editar"
                          >
                            <Edit2 size={16} />
                          </button>
                          <button
                            onClick={() => handleExcluirOrganizacao(item)}
                            disabled={loading}
                            className="text-red-600 hover:text-red-900 disabled:text-gray-400 p-1 rounded transition-colors"
                            title="Excluir"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPaginas > 1 && (
              <div className="px-6 py-4 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-500">
                    Mostrando {indiceInicial + 1} ate{" "}
                    {Math.min(indiceFinal, totalOrganizacoes)} de{" "}
                    {totalOrganizacoes} registros
                  </div>

                  <div className="flex items-center space-x-2">
                    <button
                      onClick={irParaPrimeiraPagina}
                      disabled={paginacao.paginaAtual === 1}
                      className="p-2 rounded-md border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Primeira página"
                    >
                      <ChevronsLeft size={16} />
                    </button>

                    <button
                      onClick={irParaPaginaAnterior}
                      disabled={paginacao.paginaAtual === 1}
                      className="p-2 rounded-md border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Página anterior"
                    >
                      <ChevronLeft size={16} />
                    </button>

                    <div className="flex space-x-1">
                      {gerarNumerosPaginas().map((numeroPagina) => (
                        <button
                          key={numeroPagina}
                          onClick={() => irParaPagina(numeroPagina)}
                          className={`px-3 py-2 rounded-md text-sm font-medium ${
                            paginacao.paginaAtual === numeroPagina
                              ? "bg-blue-600 text-white"
                              : "bg-white text-gray-500 border border-gray-300 hover:bg-gray-50"
                          }`}
                        >
                          {numeroPagina}
                        </button>
                      ))}
                    </div>

                    <button
                      onClick={irParaProximaPagina}
                      disabled={paginacao.paginaAtual === totalPaginas}
                      className="p-2 rounded-md border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Próxima página"
                    >
                      <ChevronRight size={16} />
                    </button>

                    <button
                      onClick={irParaUltimaPagina}
                      disabled={paginacao.paginaAtual === totalPaginas}
                      className="p-2 rounded-md border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Última página"
                    >
                      <ChevronsRight size={16} />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </PageContainer>
  );
};

export default Organizacao;
